import { useCallback, useContext, useEffect, useState } from "react";

import { isFeatureInEditableScope } from "fond/draw/modes/base/helper";

import { MapContext } from "../MapProvider";

/**
 * Custom hook that returns true when at least one selected feature belongs to
 * an editable layer. Answers: "are there any deletable features in the
 * current selection?"
 *
 * When features are selected it checks each feature's layerId against the
 * editingLayerIds array (and editableSublayers). When editingLayerIds is
 * undefined all features are considered editable (matching isFeatureInEditableScope
 * behaviour). When nothing is selected it returns false.
 *
 * Listens to draw.selectionchange, draw.layerchange, and
 * draw.editingLayersChange so it stays up-to-date as the user selects
 * features, switches layers, or toggles layer editing.
 *
 * @returns Boolean — true when the selection contains at least one editable feature
 */
export function useHasEditableSelection(): boolean {
  const { map, drawControl } = useContext(MapContext);
  const [hasEditable, setHasEditable] = useState(false);

  const check = useCallback(() => {
    const selectedFeatures = drawControl.current?.getSelected()?.features || [];

    // Nothing selected → nothing to delete
    if (selectedFeatures.length === 0) {
      setHasEditable(false);
      return;
    }

    const allLayers = drawControl.current?.options?.editOptions?.allLayers;

    // Not in multi-layer mode → all selected features are editable
    if (!allLayers || allLayers.length === 0) {
      setHasEditable(true);
      return;
    }

    const editingLayerIds: string[] | undefined = drawControl.current?.options?.editOptions?.editingLayerIds;
    const editableSublayers = drawControl.current?.options?.editOptions?.editableSublayers || [];

    // Reuse the shared helper so toolbar availability matches actual editability
    const anyEditable = selectedFeatures.some((feature) => isFeatureInEditableScope(feature, editingLayerIds, editableSublayers));

    setHasEditable(anyEditable);
  }, [drawControl]);

  useEffect(() => {
    check();

    const handleChange = () => check();

    map?.on("draw.selectionchange", handleChange);
    map?.on("draw.layerchange", handleChange);
    map?.on("draw.editingLayersChange", handleChange);

    return () => {
      map?.off("draw.selectionchange", handleChange);
      map?.off("draw.layerchange", handleChange);
      map?.off("draw.editingLayersChange", handleChange);
    };
  }, [map, check]);

  return hasEditable;
}
